import type { ParDeTokens } from '../api/operaciones';
import type { Sesion } from '../sesion/sesion';

/**
 * **Vuelta al backoffice** tras un acceso de plataforma (1.20, 0.5). `openPlatformAccess` devuelve tokens fijados al
 * negocio (G137) y el panel entra con ellos; la sesión del backoffice (sin negocio) se guarda aparte, en la misma
 * pestaña (sessionStorage, como la sesión), para volver a ella al «Salir» de la franja índigo sin pedir otra vez la
 * contraseña y el código. Si no está (otra pestaña, se borró), «Salir» acaba en el inicio de sesión.
 */
export interface VueltaAlBackoffice {
    guardar: (sesion: Sesion) => void;
    /** Los tokens del backoffice para volver a guardarlos como sesión, o null; los borra al leerlos. */
    tomar: () => ParDeTokens | null;
    borrar: () => void;
}

const CLAVE = 'reservaria.sesion.backoffice';

export function crearVueltaAlBackoffice(almacen: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null): VueltaAlBackoffice {
    // Sin almacenamiento (modo privado), solo en memoria.
    let enMemoria: Sesion | null = null;
    const borrar = () => {
        enMemoria = null;
        try {
            almacen?.removeItem(CLAVE);
        } catch {
            // Nada que borrar.
        }
    };
    const leer = (): Sesion | null => {
        if (almacen === null) {
            return enMemoria;
        }
        try {
            const guardada = JSON.parse(almacen.getItem(CLAVE) ?? 'null') as Partial<Sesion> | null;
            return guardada !== null && typeof guardada.accessToken === 'string' && typeof guardada.refreshToken === 'string' ? { accessToken: guardada.accessToken, refreshToken: guardada.refreshToken, negocio: null } : enMemoria;
        } catch {
            return enMemoria;
        }
    };
    return {
        guardar(sesion) {
            enMemoria = null;
            try {
                if (almacen === null) {
                    enMemoria = sesion;
                } else {
                    almacen.setItem(CLAVE, JSON.stringify(sesion));
                }
            } catch {
                enMemoria = sesion;
            }
        },
        tomar() {
            const sesion = leer();
            borrar();
            // El token de acceso puede haber caducado mientras se estaba dentro: el cliente lo renueva con el de refresco.
            return sesion === null ? null : { access_token: sesion.accessToken, refresh_token: sesion.refreshToken, expires_in: 0, business_id: null };
        },
        borrar,
    };
}
